import type { SelectNode } from '@workspace/db/extra'
import { useState } from 'react'
import { apiErrorMessage } from '../../../features/locations/api-errors'
import { useLocation } from '../../../features/locations/use-location'
import {
  isCrossStoreMigration,
  type OnboardVendor,
  useCancelProvisioning,
  useDeleteDevice,
  useDispatchProvisioning,
  useGenerateIpSchema,
  useLocationNodes,
  useProvisioningState,
  useRegisterDevice,
  useReplaceDevice,
} from '../../../features/locations/use-managed-devices'
import { useIdOrCode } from '../../use-id-or-code'
import { Button, ConfirmDialog, Dialog, EmptyRow, Field, Inline, inputCls, Select, SubCrumb, td, th } from './controls'
import { Badge, Card, mono, Notice, PageHeader, SkeletonCards, type Tone } from './ui'

const VENDORS: [OnboardVendor, string][] = [
  ['meraki', 'Meraki'],
  ['cloudflare', 'Cloudflare'],
  ['extreme', 'Extreme'],
  ['cradlepoint', 'Cradlepoint'],
  ['supermicro', 'Supermicro'],
]
const MERAKI_TYPES: [string, string][] = [
  ['edge', 'Edge (security appliance)'],
  ['switch', 'Switch'],
  ['ap', 'Access point'],
]
const MERAKI_NETWORK_TYPE: Record<string, string> = { edge: 'EDGE', switch: 'SWITCH', ap: 'AP' }

const phaseTone = (s: string): Tone =>
  s === 'complete' ? 'ok' : s === 'running' ? 'warn' : s === 'blocked' ? 'off' : s === 'ready' ? 'warn' : 'off'

type Ctx = { idOrCode: string; storeCode: string; orgSlug: string; locationId: string }

function RegisterDialog({ ctx, onClose }: { ctx: Ctx; onClose: () => void }) {
  const register = useRegisterDevice()
  const [vendor, setVendor] = useState<OnboardVendor>('meraki')
  const [serial, setSerial] = useState('')
  const [deviceType, setDeviceType] = useState('edge')
  const [needMove, setNeedMove] = useState(false)
  const idLabel =
    vendor === 'cradlepoint' ? 'Cradlepoint ID' : vendor === 'supermicro' ? 'MAC address' : 'Serial number'

  const submit = (confirmMove: boolean) =>
    register.mutate(
      {
        idOrCode: ctx.idOrCode,
        storeCode: ctx.storeCode,
        orgSlug: ctx.orgSlug,
        input: {
          vendor,
          serialNumber: serial,
          deviceType: vendor === 'extreme' ? deviceType : undefined,
          networkType: vendor === 'meraki' ? MERAKI_NETWORK_TYPE[deviceType] : undefined,
        },
        confirmMove,
      },
      {
        onSuccess: onClose,
        onError: (e) => setNeedMove(isCrossStoreMigration(e)),
      },
    )

  return (
    <>
      <Dialog
        open={!needMove}
        title="Register a device"
        onClose={onClose}
        footer={
          <>
            <Button variant="quiet" onClick={onClose} disabled={register.isPending}>
              Cancel
            </Button>
            <Button onClick={() => submit(false)} disabled={register.isPending || serial.trim() === ''}>
              {register.isPending ? 'Registering...' : 'Register'}
            </Button>
          </>
        }
      >
        <Field label="Vendor">
          <Select value={vendor} onChange={(v) => setVendor(v as OnboardVendor)} options={VENDORS} />
        </Field>
        {vendor === 'meraki' ? (
          <Field label="Device type">
            <Select value={deviceType} onChange={setDeviceType} options={MERAKI_TYPES} />
          </Field>
        ) : null}
        {vendor === 'extreme' ? (
          <Field label="Device type">
            <Select
              value={deviceType === 'switch' ? 'switch' : 'ap'}
              onChange={setDeviceType}
              options={[
                ['ap', 'Access point'],
                ['switch', 'Switch'],
              ]}
            />
          </Field>
        ) : null}
        <Field label={idLabel}>
          <input className={inputCls} value={serial} onChange={(e) => setSerial(e.target.value)} style={mono} />
        </Field>
        {register.error && !needMove ? <Inline tone="bad">{apiErrorMessage(register.error)}</Inline> : null}
      </Dialog>
      <ConfirmDialog
        open={needMove}
        title="Move this device from another store?"
        body="This device is currently registered to a different store. Registering it here will migrate it, and it will stop serving its previous store."
        confirmLabel="Move device"
        danger
        pending={register.isPending}
        onConfirm={() => submit(true)}
        onClose={() => setNeedMove(false)}
      />
    </>
  )
}

function ReplaceDialog({ ctx, node, onClose }: { ctx: Ctx; node: SelectNode; onClose: () => void }) {
  const replace = useReplaceDevice()
  const [serial, setSerial] = useState('')
  const [needMove, setNeedMove] = useState(false)

  const submit = (confirmMove: boolean) =>
    replace.mutate(
      {
        idOrCode: ctx.idOrCode,
        locationId: ctx.locationId,
        oldNodeSerial: node.serial ?? '',
        newNodeSerial: serial.trim(),
        nodeKind: node.kind,
        newManufacturer: node.manufacturer ?? undefined,
        extra: confirmMove ? { confirmMove: true } : undefined,
      },
      { onSuccess: onClose, onError: (e) => setNeedMove(isCrossStoreMigration(e)) },
    )

  return (
    <>
      <Dialog
        open={!needMove}
        title={`Replace ${node.name ?? node.serial}`}
        onClose={onClose}
        footer={
          <>
            <Button variant="quiet" onClick={onClose} disabled={replace.isPending}>
              Cancel
            </Button>
            <Button onClick={() => submit(false)} disabled={replace.isPending || serial.trim() === ''}>
              {replace.isPending ? 'Replacing...' : 'Replace'}
            </Button>
          </>
        }
      >
        <p className="text-[14px] text-[var(--color-muted)]">
          The new device takes over this device's role and configuration. Current serial{' '}
          <span style={mono}>{node.serial}</span>.
        </p>
        <Field label="New serial number">
          <input className={inputCls} value={serial} onChange={(e) => setSerial(e.target.value)} style={mono} />
        </Field>
        {replace.error && !needMove ? <Inline tone="bad">{apiErrorMessage(replace.error)}</Inline> : null}
      </Dialog>
      <ConfirmDialog
        open={needMove}
        title="Move the replacement from another store?"
        body="The replacement device is registered to a different store. Continuing will migrate it here."
        confirmLabel="Move and replace"
        danger
        pending={replace.isPending}
        onConfirm={() => submit(true)}
        onClose={() => setNeedMove(false)}
      />
    </>
  )
}

export function PorcelainManagedDevices() {
  const idOrCode = useIdOrCode()
  const location = useLocation(idOrCode)
  const nodes = useLocationNodes(idOrCode)
  const prov = useProvisioningState(idOrCode)
  const genSchema = useGenerateIpSchema()
  const del = useDeleteDevice()
  const dispatch = useDispatchProvisioning()
  const cancel = useCancelProvisioning()

  const [registerOpen, setRegisterOpen] = useState(false)
  const [replaceNode, setReplaceNode] = useState<SelectNode | null>(null)
  const [deleteNode, setDeleteNode] = useState<SelectNode | null>(null)
  const [needOverride, setNeedOverride] = useState(false)

  const header = (
    <PageHeader
      eyebrow={<SubCrumb idOrCode={idOrCode} section="network" page="Managed devices" />}
      title="Managed devices"
      subtitle="Hardware registered to this store, and where it is in provisioning."
    />
  )

  if (location.isLoading || nodes.isLoading)
    return (
      <section>
        {header}
        <SkeletonCards rows={3} />
      </section>
    )
  const loc = location.data
  if (location.error || nodes.error || !loc)
    return (
      <section>
        {header}
        <Notice tone="error" title="We couldn't load devices" body="Check your connection and try again in a moment." />
      </section>
    )

  const ctx: Ctx = {
    idOrCode,
    storeCode: loc.aptosStoreCode ?? loc.code ?? idOrCode,
    orgSlug: loc.org?.slug ?? loc.banner ?? '',
    locationId: loc.id,
  }
  const list = nodes.data ?? []
  const state = prov.data
  const running = state?.jobs.some((j) => j.status === 'running') ?? false
  const phases = Object.entries(state?.phases ?? {}).filter(([, p]) => p.status !== 'not_applicable')

  const runDispatch = (override: boolean) =>
    dispatch.mutate(
      { idOrCode, locationId: ctx.locationId, override },
      {
        onSuccess: () => setNeedOverride(false),
        onError: (e) => setNeedOverride(!override && apiErrorMessage(e).toLowerCase().includes('override')),
      },
    )

  return (
    <section>
      {header}
      <div className="mt-8 grid gap-5">
        <Card
          title="Provisioning"
          aside={
            state ? (
              <Badge label={state.lifecycle} tone={state.lifecycle === 'PROVISIONED' ? 'ok' : 'warn'} />
            ) : undefined
          }
        >
          {prov.isLoading ? (
            <EmptyRow>Loading provisioning state...</EmptyRow>
          ) : prov.error || !state ? (
            <EmptyRow>Provisioning state is unavailable right now.</EmptyRow>
          ) : (
            <div className="grid gap-4 p-5">
              <ol className="flex flex-wrap gap-2">
                {phases.map(([name, p]) => (
                  <li
                    key={name}
                    className="flex items-center gap-2 rounded-[10px] bg-[var(--color-surface-2)] px-3 py-2"
                  >
                    <span className="text-[13px] font-medium capitalize">{name.replace(/([A-Z])/g, ' $1')}</span>
                    <Badge label={p.status.replace('_', ' ')} tone={phaseTone(p.status)} />
                  </li>
                ))}
              </ol>
              {state.jobs.length > 0 ? (
                <ul className="grid gap-1.5 text-[13px]">
                  {state.jobs.slice(0, 4).map((j) => (
                    <li key={j.jobId} className="flex items-center justify-between gap-3">
                      <span>{j.label}</span>
                      <span className="flex items-center gap-2">
                        {j.error ? <span style={{ color: 'var(--v1-bad)' }}>{j.error}</span> : null}
                        <Badge
                          label={j.status}
                          tone={j.status === 'completed' ? 'ok' : j.status === 'failed' ? 'bad' : 'warn'}
                        />
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="quiet"
                  disabled={genSchema.isPending}
                  onClick={() => genSchema.mutate({ idOrCode, storeCode: ctx.storeCode, orgSlug: ctx.orgSlug })}
                >
                  {genSchema.isPending ? 'Generating...' : 'Generate IP schema'}
                </Button>
                <Button onClick={() => runDispatch(false)} disabled={dispatch.isPending || running}>
                  {dispatch.isPending ? 'Dispatching...' : 'Dispatch provisioning'}
                </Button>
                <Button
                  variant="danger"
                  disabled={cancel.isPending || !running}
                  onClick={() => cancel.mutate({ idOrCode, locationId: ctx.locationId })}
                >
                  {cancel.isPending ? 'Cancelling...' : 'Cancel dispatch'}
                </Button>
              </div>
              {genSchema.error ? <Inline tone="bad">{apiErrorMessage(genSchema.error)}</Inline> : null}
              {genSchema.isSuccess ? <Inline tone="ok">IP schema generated.</Inline> : null}
              {dispatch.error && !needOverride ? <Inline tone="bad">{apiErrorMessage(dispatch.error)}</Inline> : null}
              {cancel.error ? <Inline tone="bad">{apiErrorMessage(cancel.error)}</Inline> : null}
              {state.replacingSerial ? (
                <Inline tone="muted">
                  Replacement in progress for <span style={mono}>{state.replacingSerial}</span>.
                </Inline>
              ) : null}
            </div>
          )}
        </Card>

        <Card title="Devices" aside={<Button onClick={() => setRegisterOpen(true)}>Register device</Button>}>
          {list.length === 0 ? (
            <EmptyRow>No devices are registered to this store yet.</EmptyRow>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[14px]">
                <thead>
                  <tr>
                    <th className={th}>Name</th>
                    <th className={th}>Kind</th>
                    <th className={th}>Model</th>
                    <th className={th}>Serial</th>
                    <th className={th}>Status</th>
                    <th className={`${th} text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((n) => (
                    <tr
                      key={n.serial ?? n.mac ?? n.name}
                      className="transition-colors hover:bg-[var(--v1-accent-soft)]"
                    >
                      <td className={`${td} font-medium`}>{n.name ?? '-'}</td>
                      <td className={`${td} text-[var(--color-muted)]`}>
                        {String(n.kind).replace('_', ' ').toLowerCase()}
                      </td>
                      <td className={td}>{n.model ?? '-'}</td>
                      <td className={td} style={mono}>
                        {n.serial ?? '-'}
                      </td>
                      <td className={td}>
                        <Badge label={n.status ? 'Active' : 'Inactive'} tone={n.status ? 'ok' : 'off'} />
                      </td>
                      <td className={`${td} text-right`}>
                        <div className="flex justify-end gap-1">
                          <Button variant="quiet" onClick={() => setReplaceNode(n)}>
                            Replace
                          </Button>
                          <Button variant="danger" onClick={() => setDeleteNode(n)}>
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {registerOpen ? <RegisterDialog ctx={ctx} onClose={() => setRegisterOpen(false)} /> : null}
      {replaceNode ? <ReplaceDialog ctx={ctx} node={replaceNode} onClose={() => setReplaceNode(null)} /> : null}
      <ConfirmDialog
        open={deleteNode !== null}
        title={`Delete ${deleteNode?.name ?? deleteNode?.serial ?? 'device'}?`}
        body="This removes the device from the store and releases it at the vendor. This can't be undone."
        confirmLabel="Delete device"
        danger
        pending={del.isPending}
        error={del.error ? apiErrorMessage(del.error) : null}
        onConfirm={() =>
          deleteNode?.serial &&
          del.mutate(
            { idOrCode, locationId: ctx.locationId, deviceId: deleteNode.serial },
            { onSuccess: () => setDeleteNode(null) },
          )
        }
        onClose={() => {
          del.reset()
          setDeleteNode(null)
        }}
      />
      <ConfirmDialog
        open={needOverride}
        title="Dispatch with override?"
        body="Provisioning reported a precondition that isn't met. Overriding dispatches anyway and may leave devices partly configured."
        confirmLabel="Override and dispatch"
        danger
        pending={dispatch.isPending}
        onConfirm={() => runDispatch(true)}
        onClose={() => setNeedOverride(false)}
      />
    </section>
  )
}
